import { ClockCircleOutlined, IdcardOutlined, SafetyOutlined, BankOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon, UserIcon } from 'assets/images/svg';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCredentialTypes, selectCardTemplatesIR } from 'reduxStore/types/selectors';
import { ManageCredentialDropdown } from 'zComponents/molecules';
import { Col, Divider, Drawer, Flex, Row, Spin, Tag, Typography } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import {
	AssigningTimeViewer,
	CredentialDrawerSkeleton,
	EditDrawerHeader,
	EditSiteSelect
} from './components';
import { CREDENTIAL_TYPE_PASSCODE_CARD } from 'config/credentialTypes';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { getCredentialStatus } from 'library/helpers/credentials';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';

dayjs.extend(isBetween);

const { Text } = Typography;

const CredentialDrawer = ({ visible: visibleProp, setVisible: setVisibleProp, credentialID }) => {
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	const {
		token: { padding, paddingXXL = 48, paddingMD, paddingLG, borderRadiusLG, fontSizeLG, colorTextLabel }
	} = theme.useToken();

	// 🔥 `credentialWithSiblings` is the grouped record — used for fields that
	// span across siblings (integrations[] for dual-tech cards). Scoped to
	// role-visible credentials so RBAC is consistent with CredentialsTab.
	const credential = useSelector((state) => selectCredentialByID(state, credentialID));

	const person = useSelector((state) => selectPeopleByPersonID(state, credential?.personID));
	const credentialTypes = useSelector(selectCredentialTypes);
	const cardTemplatesIR = useSelector(selectCardTemplatesIR);
	const { completeDeleteCredential } = useSelector((state) => state.Credentials);
	const credentialLoadingType = useSelector((state) => selectCredentialLoading(state, credentialID));
	const isCredentialLoading = !!credentialLoadingType;

	const useMemoizedCredentialType = useMemo(() => {
		if (credential && credentialTypes.length > 0) {
			return credentialTypes.find((type) => type.credentialTypeID === credential.credentialTypeID);
		}
		return null;
	}, [credential, credentialTypes]);

	// ✅ Close
	const handleOnClose = () => {
		setVisible(false);
	};

	const prevCompleteRef = useRef(completeDeleteCredential);
	useEffect(() => {
		// Only close if completeDeleteCredential just flipped from false → true
		if (completeDeleteCredential && !prevCompleteRef.current) {
			setVisible(false);
		}
		prevCompleteRef.current = completeDeleteCredential;
	}, [completeDeleteCredential, setVisible]);

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	const statusInfo = getCredentialStatus(credential);

	// Resolve site code label for IR credentials
	const isIRCredential =
		credential?.integrationID === INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id ||
		credential?.integrations?.some((i) => i.integrationID === INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id);

	const siteCodeLabel = useMemo(() => {
		if (!isIRCredential || !credential?.externalTemplateID) return null;
		const template = cardTemplatesIR.find((t) => t.addressID === credential.externalTemplateID);
		if (!template) return credential.externalTemplateID;
		const name = template.name || '';
		const code = template.siteCode || '';
		if (name && code) return `${name} (${code})`;
		return name || code;
	}, [isIRCredential, credential?.externalTemplateID, cardTemplatesIR]);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			destroyOnClose
			styles={drawerStyles}
		>
			{!credential ? (
				<CredentialDrawerSkeleton />
			) : (
				<Flex vertical>
					<EditDrawerHeader credentialID={credentialID} />

					<div style={{ marginTop: paddingXXL }}>
						<ManageCredentialDropdown credentialID={credentialID} />
					</div>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					<Row>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: padding }}>
								<InfoTuple
									icon={<BankOutlined />}
									text='Sites'
									align='start'
									value={<EditSiteSelect credentialID={credentialID} />}
								/>
								<InfoTuple
									icon={<CreditCardIcon />}
									text='Category'
									value={
										<Text
											style={{
												fontSize: fontSizeLG,
												color: colorTextLabel,
												lineHeight: '24px'
											}}
										>
											{useMemoizedCredentialType?.name || '--'}
										</Text>
									}
								/>
								<InfoTuple
									icon={<UserIcon />}
									text='Assigned Person'
									value={
										isCredentialLoading ? (
											<Spin size='small' />
										) : credential.personID ? (
											`${person?.displayName || ''}`.trim()
										) : (
											`-`
										)
									}
								/>

								{credential.credentialTypeID === CREDENTIAL_TYPE_PASSCODE_CARD && (
									<>
										<InfoTuple
											icon={<IdcardOutlined />}
											text='Serial Number'
											value={
												<Text
													style={{
														fontSize: fontSizeLG,
														color: colorTextLabel,
														lineHeight: '24px'
													}}
												>
													{credential.uniqueIdentifier || '-'}
												</Text>
											}
										/>
										{credential.externalCredentialTemplateID && (
											<InfoTuple
												icon={<CreditCardIcon />}
												text='Card Template'
												value={
													<Text
														style={{
															fontSize: fontSizeLG,
															color: colorTextLabel,
															lineHeight: '24px'
														}}
													>
														{cardTemplatesIR.find(
															(t) =>
																t.credentialTemplateID ===
																credential.externalCredentialTemplateID
														)?.name || '-'}
													</Text>
												}
											/>
										)}
										{isIRCredential && siteCodeLabel && (
											<InfoTuple
												icon={<BankOutlined />}
												text='Site Code'
												value={
													<Text
														style={{
															fontSize: fontSizeLG,
															color: colorTextLabel,
															lineHeight: '24px'
														}}
													>
														{siteCodeLabel}
													</Text>
												}
											/>
										)}
									</>
								)}

								<InfoTuple
									icon={<SafetyOutlined />}
									text='Status'
									value={
										isCredentialLoading ? (
											<Spin size='small' />
										) : (
											(() => {
												const statusStyles = {
													Active: { color: '#D1FFDC', textColor: '#059641' },
													Expired: { color: '#FFD7D3', textColor: '#FF5949' },
													Scheduled: { color: '#FFF7E6', textColor: '#D48806' }
												};
												const s = statusStyles[statusInfo.text] || {
													color: '#F5F5F5',
													textColor: '#8C8C8C'
												};
												return (
													<Tag
														color={s.color}
														bordered={false}
														style={{ color: s.textColor, fontWeight: 600, margin: 0 }}
													>
														{statusInfo.text}
													</Tag>
												);
											})()
										)
									}
								/>

								<InfoTuple
									icon={<ClockCircleOutlined />}
									text='Validity Period'
									value={
										isCredentialLoading ? (
											<Spin size='small' />
										) : (
											<AssigningTimeViewer
												startDate={credential.startDate}
												endDate={credential.endDate}
											/>
										)
									}
								/>
							</Flex>
						</Col>
					</Row>

				</Flex>
			)}
		</Drawer>
	);
};

export default CredentialDrawer;
